import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Send, X, Search, Paperclip, Trash2, FileText, MapPin, Users, MessageSquare, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { UOM_OPTIONS, INCOTERM_OPTIONS, EnquiryOpenBadge, CATEGORY_OPTIONS } from "@/lib/enquiries";
import AuctionFields, { DEFAULT_AUCTION_CFG, auctionPayload } from "@/components/AuctionFields";
import TermsEditor from "@/components/TermsEditor";

const emptyItem = () => ({ material_code: "", description: "", quantity: "", uom: "EA", category: "" });
const emptyAddr = { line1: "", line2: "", city: "", state: "", country: "India", postal_code: "", contact_name: "", contact_number: "", contact_email: "" };
const EMPTY = {
  title: "", description: "", incoterms: "EXW", delivery_days: "", start_at: "", deadline: "",
  supplier_ids: [], items: [emptyItem()], buyer_terms_conditions: "", buyer_comment: "", delivery_address: { ...emptyAddr },
};
const MAX_FILE_MB = 15;
const ALLOWED_EXT = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".csv", ".jpg", ".jpeg", ".png", ".zip"];

const Section = ({ num, title, required, children, testid }) => (
  <div className="rounded-lg border" data-testid={testid}>
    <p className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
      Section {num} — {title} {required && <span className="text-red-600">*</span>}
    </p>
    <div className="space-y-3 p-4">{children}</div>
  </div>
);

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [draftId, setDraftId] = useState(null);
  const [auctionEnabled, setAuctionEnabled] = useState(false);
  const [auctionCfg, setAuctionCfg] = useState(DEFAULT_AUCTION_CFG);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const [selectedSellers, setSelectedSellers] = useState([]); // [{id,name,email,gst_number}]
  const [files, setFiles] = useState([]); // [{file, category}]
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/enquiries").then((r) => setEnquiries(r.data.enquiries)).catch(() => toast.error("Failed to load enquiries")), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get(`/suppliers/search?q=${encodeURIComponent(searchQ)}`).then((r) => setSearchResults(r.data.suppliers || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, open]);

  const toggleSupplier = (s) => {
    setSelectedSellers((sel) => sel.some((x) => x.id === s.id) ? sel.filter((x) => x.id !== s.id) : [...sel, s]);
  };
  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, delivery_address: { ...f.delivery_address, [k]: v } }));

  const stageFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const bad = picked.find((f) => !ALLOWED_EXT.includes(`.${f.name.split(".").pop().toLowerCase()}`));
    if (bad) return toast.error(`${bad.name}: file type not allowed`);
    const big = picked.find((f) => f.size > MAX_FILE_MB * 1048576);
    if (big) return toast.error(`${big.name}: exceeds ${MAX_FILE_MB} MB limit`);
    setFiles((fs) => [...fs, ...picked.map((f) => ({ file: f, category: "technical" }))]);
    e.target.value = "";
  };

  const validate = () => {
    if (!form.items.some((it) => it.material_code.trim() && it.description.trim() && Number(it.quantity) > 0)) return "Add at least one valid line item";
    if (!form.deadline) return "Event timer is required";
    if (!form.delivery_days) return "Required delivery days is required";
    const a = form.delivery_address;
    if (!a.line1.trim() || !a.city.trim() || !a.state.trim() || !a.country.trim() || !a.postal_code.trim()) return "Delivery address is required.";
    if (!selectedSellers.length) return "Please select at least one Seller.";
    if (!form.buyer_terms_conditions.trim()) return "Buyer Terms & Conditions are mandatory.";
    return null;
  };

  const payload = (draft) => ({
    title: form.title, description: form.description || form.items[0]?.description || "",
    incoterms: form.incoterms, delivery_days: Number(form.delivery_days) || 1,
    deadline: form.deadline ? new Date(form.deadline).toISOString() : new Date(Date.now() + 86400000).toISOString(),
    start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
    supplier_ids: selectedSellers.map((s) => s.id),
    items: form.items.filter((it) => it.material_code.trim()).map(({ _k, ...it }) => ({ ...it, quantity: Number(it.quantity) || 0 })),
    buyer_terms_conditions: form.buyer_terms_conditions, buyer_comment: form.buyer_comment,
    delivery_address: form.delivery_address, save_draft: draft,
    ...(auctionEnabled ? { auction: auctionPayload(auctionCfg) } : {}),
  });

  const uploadStaged = async (enquiryId) => {
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f.file);
      fd.append("category", f.category);
      await api.post(`/enquiries/${enquiryId}/attachments`, fd);
    }
  };

  const reset = () => { setForm(EMPTY); setSelectedSellers([]); setFiles([]); setDraftId(null); setAuctionEnabled(false); setAuctionCfg(DEFAULT_AUCTION_CFG); setSearchQ(""); };

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (draftId) {
        await api.put(`/enquiries/${draftId}/draft`, { ...payload(true), delivery_days: Number(form.delivery_days) || 1 });
        await uploadStaged(draftId);
        toast.success("Draft updated");
      } else {
        const r = await api.post("/enquiries", payload(true));
        await uploadStaged(r.data.id);
        toast.success(`Draft ${r.data.enquiry_number} saved — not yet shared with sellers`);
      }
      setFiles([]);
      setOpen(false); reset(); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const publish = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      let id = draftId;
      if (draftId) {
        await api.put(`/enquiries/${draftId}/draft`, { ...payload(true), delivery_days: Number(form.delivery_days) || 1 });
      } else {
        const r = await api.post("/enquiries", payload(true));
        id = r.data.id;
      }
      await uploadStaged(id);
      const r = await api.post(`/enquiries/${id}/publish`);
      toast.success(`Enquiry published — ${r.data.invites.length} seller(s) invited`);
      setOpen(false); reset();
      navigate(`/buyer/enquiries/${id}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const openDraft = async (enq) => {
    if (enq.status !== "draft") return navigate(`/buyer/enquiries/${enq.id}`);
    try {
      const r = await api.get(`/enquiries/${enq.id}`);
      const d = r.data.enquiry;
      setForm({
        title: d.title || "", description: d.description || "", incoterms: d.incoterms, delivery_days: d.delivery_days,
        start_at: d.start_at ? d.start_at.slice(0, 16) : "", deadline: d.deadline ? d.deadline.slice(0, 16) : "",
        supplier_ids: d.draft_supplier_ids || [], items: (d.items || []).map((it) => ({ _k: crypto.randomUUID(), material_code: it.material_code, description: it.description, quantity: it.quantity, uom: it.uom })),
        buyer_terms_conditions: d.buyer_terms_conditions || "", buyer_comment: d.buyer_comment || "",
        delivery_address: { ...emptyAddr, ...(d.delivery_address || {}) },
      });
      const ids = new Set(d.draft_supplier_ids || []);
      const sr = await api.get("/suppliers/search?q=");
      setSelectedSellers((sr.data.suppliers || []).filter((s) => ids.has(s.id)));
      if (d.draft_auction) { setAuctionEnabled(true); setAuctionCfg({ ...DEFAULT_AUCTION_CFG, ...d.draft_auction }); }
      setDraftId(d.id);
      setOpen(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const drafts = enquiries.filter((e) => e.status === "draft");
  const published = enquiries.filter((e) => e.status !== "draft");

  return (
    <div data-testid="enquiries-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Enquiries</h1>
          <p className="text-sm text-muted-foreground" data-testid="enquiries-count">
            {published.length} floated{drafts.length > 0 && ` · ${drafts.length} draft${drafts.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <Button data-testid="float-enquiry-btn" onClick={() => { reset(); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="mr-1.5 h-4 w-4" /> Float an Enquiry
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="enquiries-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Enquiry #</th>
              <th className="px-4 py-3 font-medium">Material</th>
              <th className="px-4 py-3 font-medium text-right">Quantity</th>
              <th className="px-4 py-3 font-medium">Incoterms</th>
              <th className="px-4 py-3 font-medium">Timer</th>
              <th className="px-4 py-3 font-medium text-right">Invited</th>
              <th className="px-4 py-3 font-medium text-right">Participated</th>
              <th className="px-4 py-3 font-medium text-right">Declined</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {enquiries.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground" data-testid="enquiries-empty">
                No enquiries yet. Float your first enquiry to invite sellers to quote.
              </td></tr>
            )}
            {enquiries.map((enq) => (
              <tr key={enq.id} data-testid={`enquiry-row-${enq.enquiry_number}`} onClick={() => openDraft(enq)}
                className="cursor-pointer border-b transition-colors last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs font-semibold">{enq.enquiry_number}</td>
                <td className="px-4 py-3">
                  <p className="font-medium">{enq.title || enq.material_code}{(enq.items?.length || 0) > 1 && <span className="ml-1 text-xs font-normal text-muted-foreground">+{enq.items.length - 1} more</span>}</p>
                  <p className="max-w-56 truncate text-xs text-muted-foreground" title={enq.description}>{enq.description}</p>
                </td>
                <td className="px-4 py-3 text-right font-mono">{(enq.items?.length || 0) > 1 ? `${enq.items.length} items` : `${enq.quantity} ${enq.uom}`}</td>
                <td className="px-4 py-3 font-mono text-xs">{enq.incoterms}</td>
                <td className="px-4 py-3 text-xs" data-testid={`timer-cell-${enq.enquiry_number}`} title={fmtDateTime(enq.deadline)}>
                  {(() => {
                    const rem = Math.max(0, Math.floor((new Date(enq.deadline).getTime() - now) / 1000));
                    const d = Math.floor(rem / 86400), h = Math.floor((rem % 86400) / 3600), m = Math.floor((rem % 3600) / 60), s = rem % 60;
                    const pad = (n) => String(n).padStart(2, "0");
                    return rem <= 0
                      ? <span className="font-semibold text-[#991B1B]">Time expired</span>
                      : <span className={`font-mono font-medium tabular-nums ${rem < 3600 ? "text-[#991B1B]" : rem < 86400 ? "text-[#92400E]" : "text-slate-700"}`}>{pad(d)}d : {pad(h)}h : {pad(m)}m : {pad(s)}s</span>;
                  })()}
                </td>
                <td className="px-4 py-3 text-right font-mono">{enq.invited_count}</td>
                <td className="px-4 py-3 text-right font-mono text-[#166534]">{enq.participated_count}</td>
                <td className="px-4 py-3 text-right font-mono text-[#991B1B]">{enq.declined_count}</td>
                <td className="px-4 py-3">
                  {enq.status === "draft"
                    ? <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600" data-testid={`draft-badge-${enq.enquiry_number}`}>Draft</span>
                    : <EnquiryOpenBadge passed={enq.deadline_passed} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-3xl" data-testid="float-enquiry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Send className="h-4 w-4" /> {draftId ? "Edit Draft Enquiry" : "Float an Enquiry"}</DialogTitle>
            <DialogDescription>Complete the sections below. Sellers are only invited when you publish.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Section num={1} title="Event Details" required testid="section-event-details">
              <div><Label>Event Title</Label><Input data-testid="enquiry-form-title" className="mt-1.5" placeholder="e.g. Industrial Valve Procurement" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Event Description</Label><Textarea data-testid="enquiry-form-description" rows={2} className="mt-1.5" placeholder="Overview / instructions visible to sellers" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div><Label>Start Date/Time</Label><Input data-testid="enquiry-form-start" type="datetime-local" className="mt-1.5" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></div>
                <div><Label>End Date/Time (Deadline) *</Label><Input data-testid="enquiry-form-deadline" required type="datetime-local" className="mt-1.5" min={new Date(Date.now() + 3600000).toISOString().slice(0, 16)} value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
                <div><Label>Incoterms *</Label>
                  <Select value={form.incoterms} onValueChange={(v) => setForm({ ...form, incoterms: v })}>
                    <SelectTrigger data-testid="enquiry-form-incoterms" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{INCOTERM_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Delivery Required (days) *</Label><Input data-testid="enquiry-form-delivery-days" required type="number" min="1" className="mt-1.5" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} /></div>
              </div>
              <div className="rounded-md border p-3">
                <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium">
                  <Checkbox data-testid="enable-auction-toggle" checked={auctionEnabled} onCheckedChange={(v) => setAuctionEnabled(!!v)} />
                  Enable Auction <span className="text-xs font-normal text-muted-foreground">(optional e-auction stage for this event)</span>
                </label>
                {auctionEnabled && <div className="mt-3 border-t pt-3"><AuctionFields cfg={auctionCfg} onChange={setAuctionCfg} /></div>}
              </div>
            </Section>

            <Section num={2} title="Line Items" required testid="section-line-items">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{form.items.length} item(s)</p>
                <Button type="button" variant="outline" size="sm" data-testid="enquiry-add-item-btn" onClick={addItem}><Plus className="mr-1 h-3.5 w-3.5" /> Add Line Item</Button>
              </div>
              <div className="space-y-3" data-testid="enquiry-items-editor">
                {form.items.map((it, i) => (
                  <div key={it._k} className="relative rounded-md border p-3" data-testid={`enquiry-item-${i}`}>
                    {form.items.length > 1 && (
                      <button type="button" data-testid={`enquiry-remove-item-${i}`} onClick={() => removeItem(i)}
                        className="absolute right-2 top-2 text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Material Code *</Label><Input data-testid={`enquiry-item-code-${i}`} className="mt-1 font-mono" placeholder="STL-ROD-8MM" value={it.material_code} onChange={(e) => setItem(i, { material_code: e.target.value })} /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Quantity *</Label><Input data-testid={`enquiry-item-qty-${i}`} type="number" min="0.01" step="any" className="mt-1" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} /></div>
                        <div><Label className="text-xs">UOM *</Label>
                          <Select value={it.uom} onValueChange={(v) => setItem(i, { uom: v })}>
                            <SelectTrigger data-testid={`enquiry-item-uom-${i}`} className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">{UOM_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Category</Label>
                          <Select value={it.category || "none"} onValueChange={(v) => setItem(i, { category: v === "none" ? "" : v })}>
                            <SelectTrigger data-testid={`enquiry-item-category-${i}`} className="mt-1"><SelectValue placeholder="Auto" /></SelectTrigger>
                            <SelectContent className="bg-white"><SelectItem value="none">Auto-detect</SelectItem>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2"><Label className="text-xs">Description *</Label><Textarea data-testid={`enquiry-item-desc-${i}`} rows={1} className="mt-1" placeholder="Material description / specifications" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} /></div>
                  </div>
                ))}
              </div>
            </Section>

            <Section num={3} title="Delivery Address" required testid="section-delivery-address">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs">Address Line 1 *</Label><Input data-testid="addr-line1" className="mt-1" placeholder="Shakti Precision Industries Pvt Ltd" value={form.delivery_address.line1} onChange={(e) => setAddr("line1", e.target.value)} /></div>
                <div className="col-span-2"><Label className="text-xs">Address Line 2</Label><Input data-testid="addr-line2" className="mt-1" placeholder="Plot No. 14, Industrial Area" value={form.delivery_address.line2} onChange={(e) => setAddr("line2", e.target.value)} /></div>
                <div><Label className="text-xs">City *</Label><Input data-testid="addr-city" className="mt-1" value={form.delivery_address.city} onChange={(e) => setAddr("city", e.target.value)} /></div>
                <div><Label className="text-xs">State *</Label><Input data-testid="addr-state" className="mt-1" value={form.delivery_address.state} onChange={(e) => setAddr("state", e.target.value)} /></div>
                <div><Label className="text-xs">Country *</Label><Input data-testid="addr-country" className="mt-1" value={form.delivery_address.country} onChange={(e) => setAddr("country", e.target.value)} /></div>
                <div><Label className="text-xs">PIN / Postal Code *</Label><Input data-testid="addr-postal" className="mt-1" value={form.delivery_address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} /></div>
                <div><Label className="text-xs">Delivery Contact Person</Label><Input data-testid="addr-contact-name" className="mt-1" value={form.delivery_address.contact_name} onChange={(e) => setAddr("contact_name", e.target.value)} /></div>
                <div><Label className="text-xs">Contact Number</Label><Input data-testid="addr-contact-number" className="mt-1" placeholder="+91 …" value={form.delivery_address.contact_number} onChange={(e) => setAddr("contact_number", e.target.value)} /></div>
                <div className="col-span-2"><Label className="text-xs">Delivery Email</Label><Input data-testid="addr-contact-email" type="email" className="mt-1" value={form.delivery_address.contact_email} onChange={(e) => setAddr("contact_email", e.target.value)} /></div>
              </div>
            </Section>

            <Section num={4} title="Invite Sellers" required testid="section-invite-sellers">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="seller-search-input" className="pl-8" placeholder="Search sellers by name, company, GST number, email" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
              </div>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="enquiry-form-suppliers">
                {searchResults.length === 0 && <p className="p-2 text-sm text-muted-foreground">No sellers found. Add suppliers first.</p>}
                {searchResults.map((s) => {
                  const sel = selectedSellers.some((x) => x.id === s.id);
                  const allowed = s.selectable !== false;
                  return (
                    <label key={s.id} className={`flex items-center gap-2.5 rounded px-2 py-1.5 text-sm ${allowed ? "cursor-pointer hover:bg-slate-50" : "cursor-not-allowed opacity-70"}`} data-testid={`seller-result-${s.id.slice(0, 8)}`}>
                      <Checkbox data-testid={`enquiry-invite-${s.name.replace(/\W+/g, "-").toLowerCase()}`} checked={sel} disabled={!allowed} onCheckedChange={() => allowed && toggleSupplier(s)} />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium">{s.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">GST: {s.gst_number || "—"} · {s.email}</span>
                      </span>
                      {s.relationship_status === "pending_approval" && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`seller-pending-${s.id.slice(0, 8)}`}>Supplier approval is pending</span>}
                      {s.relationship_status === "rejected" && <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">Rejected</span>}
                      {s.relationship_status === "not_connected" && <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Not connected — request approval</span>}
                      {s.relationship_status === "inactive" && <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Inactive</span>}
                      {sel && <span className="text-xs font-medium text-[#166534]">Selected</span>}
                    </label>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium" data-testid="selected-sellers-counter">
                  {selectedSellers.length} Seller{selectedSellers.length === 1 ? "" : "s"} Selected
                </span>
                {selectedSellers.map((s) => (
                  <span key={s.id} className="flex items-center gap-1.5 rounded-full border bg-slate-50 px-2.5 py-1 text-xs" data-testid={`selected-seller-${s.id.slice(0, 8)}`}>
                    ✓ {s.name}
                    <button type="button" data-testid={`remove-selected-${s.id.slice(0, 8)}`} onClick={() => toggleSupplier(s)} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </Section>

            <Section num={5} title="Attachments" testid="section-attachments">
              <p className="text-xs text-muted-foreground">Attachments (Optional) — attach drawings, specifications, BOQs, technical documents, images or other supporting files (PDF, DOC, XLS, CSV, JPG, PNG, ZIP · max {MAX_FILE_MB} MB each).</p>
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="enquiry-attachments-input" accept={ALLOWED_EXT.join(",")} onChange={stageFiles} />
              <Button type="button" variant="outline" size="sm" data-testid="add-attachment-btn" onClick={() => fileRef.current?.click()}>
                <Paperclip className="mr-1.5 h-3.5 w-3.5" /> + Add Attachment
              </Button>
              {files.map((f, i) => (
                <div key={`${f.file.name}-${f.file.size}`} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm" data-testid={`staged-file-${i}`}>
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate font-medium">{f.file.name}</span>
                  <span className="text-xs text-muted-foreground">{fmtSize(f.file.size)} · {f.file.name.split(".").pop().toUpperCase()}</span>
                  <Select value={f.category} onValueChange={(v) => setFiles(files.map((x, xi) => xi === i ? { ...x, category: v } : x))}>
                    <SelectTrigger data-testid={`staged-file-cat-${i}`} className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <button type="button" data-testid={`staged-file-remove-${i}`} onClick={() => setFiles(files.filter((_, xi) => xi !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </Section>

            <Section num={6} title="Buyer Terms & Conditions" required testid="section-terms">
              <TermsEditor value={form.buyer_terms_conditions} onChange={(v) => setForm({ ...form, buyer_terms_conditions: v })} testid="buyer-terms-editor" />
              {!form.buyer_terms_conditions.trim() && <p className="text-xs text-muted-foreground">Mandatory — sellers must accept these before submitting a quotation.</p>}
            </Section>

            <Section num={7} title="Buyer Comment" testid="section-buyer-comment">
              <Textarea data-testid="buyer-comment-input" rows={2} placeholder="Add any additional information or instructions for Sellers… (optional)" value={form.buyer_comment} onChange={(e) => setForm({ ...form, buyer_comment: e.target.value })} />
            </Section>

            <Section num={8} title="Review & Publish" testid="section-review">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4" data-testid="review-summary">
                <div><p className="text-xs text-muted-foreground">Event</p><p className="font-medium">{form.title || form.items[0]?.material_code || "—"}</p></div>
                <div><p className="text-xs text-muted-foreground">Timeline</p><p className="font-medium">{form.deadline ? fmtDateTime(new Date(form.deadline).toISOString()) : "—"}</p></div>
                <div><p className="text-xs text-muted-foreground">Line Items</p><p className="font-medium" data-testid="review-items-count">{form.items.filter((i) => i.material_code.trim()).length}</p></div>
                <div><p className="text-xs text-muted-foreground">Sellers Invited</p><p className="font-medium" data-testid="review-sellers-count">{selectedSellers.length}</p></div>
                <div><p className="text-xs text-muted-foreground">Delivery</p><p className="font-medium" data-testid="review-delivery">{[form.delivery_address.city, form.delivery_address.state].filter(Boolean).join(", ") || "—"}</p></div>
                <div><p className="text-xs text-muted-foreground">Attachments</p><p className="font-medium">{files.length}</p></div>
                <div><p className="text-xs text-muted-foreground">Terms & Conditions</p><p className={`font-medium ${form.buyer_terms_conditions.trim() ? "text-[#166534]" : "text-[#991B1B]"}`} data-testid="review-terms">{form.buyer_terms_conditions.trim() ? "Added ✓" : "Missing *"}</p></div>
                <div><p className="text-xs text-muted-foreground">Buyer Comment</p><p className="font-medium">{form.buyer_comment.trim() ? "Added" : "—"}</p></div>
              </div>
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button type="button" variant="outline" data-testid="save-draft-btn" disabled={saving} onClick={saveDraft}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Draft
                </Button>
                <Button type="button" data-testid="publish-enquiry-btn" disabled={saving} onClick={publish} className="bg-slate-900 hover:bg-slate-800">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Publish Enquiry
                </Button>
              </div>
            </Section>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
